import { motion } from "framer-motion";
import { ScrollReveal } from "@/components/ScrollReveal";
import { CONTACT_DATA, PRIVACY_POLICY_DATA } from "@/data/siteData";

const PolicySection = ({ title, children }) => (
  <div className="space-y-3">
    <h2 className="font-['Outfit'] text-xl md:text-2xl font-semibold tracking-tight text-[#0A0A0A]">
      {title}
    </h2>
    <div className="text-sm md:text-base text-[#475569] leading-relaxed space-y-3">{children}</div>
  </div>
);

export default function PrivacyPolicy() {
  return (
    <div>
      <section className="relative py-24 md:py-32 overflow-hidden mesh-bg" data-testid="privacy-hero">
        <div className="absolute inset-0 grain-overlay" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl"
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#097DC3]/8 text-[#097DC3] text-sm font-semibold uppercase tracking-wider mb-6">
              {PRIVACY_POLICY_DATA.hero.tagline}
            </span>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-[#0A0A0A] mb-6">
              {PRIVACY_POLICY_DATA.hero.titlePrefix} <span className="text-[#097DC3]">{PRIVACY_POLICY_DATA.hero.titleHighlight}</span>
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-[#475569] max-w-2xl">
              {PRIVACY_POLICY_DATA.hero.subtitle}
            </p>
            <p className="text-sm text-slate-500 mt-6">Last updated: {PRIVACY_POLICY_DATA.lastUpdated}</p>
          </motion.div>
        </div>
        <div className="absolute top-20 right-10 w-72 h-72 rounded-full bg-[#8CDDF6]/15 blur-3xl" />
      </section>

      <section className="py-16 md:py-24 bg-white" data-testid="privacy-content">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="space-y-12">
            <ScrollReveal>
              <PolicySection title="1) Who We Are">
                <p>
                  “Uproot Innovation”, “we”, “us”, or “our” refers to Uproot Innovation (based in {CONTACT_DATA.office.address}).
                  This Privacy Policy applies to our website and any pages, forms, or services that link to it.
                </p>
              </PolicySection>
            </ScrollReveal>

            <ScrollReveal delay={0.05}>
              <PolicySection title="2) Information We Collect">
                <p>We collect information in the following ways:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>
                    <span className="font-medium text-[#0A0A0A]">Information you provide:</span> when you submit a contact inquiry or form, you may provide your name, email, phone number, subject, and message.
                  </li>
                  <li>
                    <span className="font-medium text-[#0A0A0A]">Program participation & feedback:</span> if you submit a testimonial or feedback, you may provide your name, role, program, category, and experience/outcome.
                  </li>
                  <li>
                    <span className="font-medium text-[#0A0A0A]">Automatically collected data:</span> basic technical data (such as device type, browser, approximate location, and usage events) may be collected by our hosting and analytics tooling.
                  </li>
                </ul>
              </PolicySection>
            </ScrollReveal>

            <ScrollReveal delay={0.1}>
              <PolicySection title="3) How We Use Information">
                <p>We use personal information to:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Respond to inquiries and provide requested information.</li>
                  <li>Operate, maintain, and improve our website and offerings.</li>
                  <li>Manage programs (such as student tracks, mentorship, workshops, or partnerships).</li>
                  <li>Review, moderate, and (when approved) display testimonials or feedback.</li>
                  <li>Detect, prevent, and investigate security incidents, abuse, or technical issues.</li>
                </ul>
              </PolicySection>
            </ScrollReveal>

            <ScrollReveal delay={0.15}>
              <PolicySection title="4) Cookies and Similar Technologies">
                <p>
                  Our website may use cookies and similar technologies that are necessary to deliver content, remember preferences, and understand website usage.
                  You can usually control cookies through your browser settings.
                </p>
              </PolicySection>
            </ScrollReveal>

            <ScrollReveal delay={0.2}>
              <PolicySection title="5) How We Share Information">
                <p>We may share information in limited circumstances:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>
                    <span className="font-medium text-[#0A0A0A]">Service providers:</span> trusted vendors that help us run our website (hosting, form delivery, databases, communications). They process data only on our instructions.
                  </li>
                  <li>
                    <span className="font-medium text-[#0A0A0A]">Legal and safety:</span> if required by law, or to protect rights, safety, and security.
                  </li>
                  <li>
                    <span className="font-medium text-[#0A0A0A]">Business transfers:</span> if we undergo a merger, acquisition, or restructuring, information may be transferred as part of that transaction.
                  </li>
                </ul>
                <p>We do not sell personal information.</p>
              </PolicySection>
            </ScrollReveal>

            <ScrollReveal delay={0.25}>
              <PolicySection title="6) Data Retention">
                <p>
                  We retain personal information only as long as reasonably necessary for the purposes described in this policy,
                  including to meet legal, accounting, or reporting requirements.
                </p>
              </PolicySection>
            </ScrollReveal>

            <ScrollReveal delay={0.3}>
              <PolicySection title="7) Security">
                <p>
                  We implement reasonable technical and organizational measures to help protect personal information.
                  However, no method of transmission or storage is completely secure.
                </p>
              </PolicySection>
            </ScrollReveal>

            <ScrollReveal delay={0.35}>
              <PolicySection title="8) Your Choices and Rights">
                <p>
                  Depending on your location and applicable law, you may have rights to access, correct, delete, or object to certain processing of your personal information.
                  To make a request, contact us using the details below.
                </p>
              </PolicySection>
            </ScrollReveal>

            <ScrollReveal delay={0.4}>
              <PolicySection title="9) Children’s Privacy">
                <p>
                  Our website is not intended for children under 13. If you believe a child has provided personal information, please contact us so we can take appropriate steps.
                </p>
              </PolicySection>
            </ScrollReveal>

            <ScrollReveal delay={0.45}>
              <PolicySection title="10) Contact Us">
                <p>
                  For questions about this Privacy Policy or to make a privacy request, contact us at:
                </p>
                <div className="rounded-2xl border border-slate-100 bg-[#F8FAFC] p-5">
                  <p className="text-sm md:text-base text-[#0A0A0A] font-medium">Uproot Innovation</p>
                  <p className="text-sm md:text-base">Email: {CONTACT_DATA.office.email}</p>
                  <p className="text-sm md:text-base">Phone: {CONTACT_DATA.office.phone}</p>
                </div>
              </PolicySection>
            </ScrollReveal>

            <ScrollReveal delay={0.5}>
              <PolicySection title="11) Changes to This Policy">
                <p>
                  We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated “Last updated” date.
                </p>
              </PolicySection>
            </ScrollReveal>
          </div>
        </div>
      </section>
    </div>
  );
}
